import { FC, PropsWithChildren, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import {
    DiscountService,
    DEFAULT_DISCOUNT_TIMEZONE,
    type TreezSpecial,
} from "../services/discount.service";

import { DiscountsContext } from "./DiscountsContext";

type DiscountsProviderProps = PropsWithChildren<{
    /** When missing, the query is disabled and discounts stay empty. */
    entityId?: string;
    storeShortName?: string;
    timezone?: string;
}>;

const STALE_MS = 60 * 1000;

const DiscountsProvider: FC<DiscountsProviderProps> = ({
    children,
    entityId,
    storeShortName,
    timezone = DEFAULT_DISCOUNT_TIMEZONE,
}) => {
    const tz = timezone || DEFAULT_DISCOUNT_TIMEZONE;

    const service = useMemo(() => {
        if (!entityId) {
            return null;
        }
        return new DiscountService(entityId, tz);
    }, [entityId, tz]);

    const queryKey = [
        "discounts",
        storeShortName || entityId || "none",
        tz,
    ] as const;

    const query = useQuery<TreezSpecial[]>({
        queryKey: [...queryKey],
        queryFn: async ({ signal }) => {
            if (!service) {
                return [];
            }
            return service.getDiscounts(signal);
        },
        enabled: Boolean(entityId && service),
        staleTime: STALE_MS,
    });

    const value = useMemo(
        () => ({
            discounts: query.data ?? [],
            isLoading: query.isLoading,
            isFetching: query.isFetching,
            isError: query.isError,
            error: query.error ?? null,
            refetch: query.refetch,
        }),
        [
            query.data,
            query.isLoading,
            query.isFetching,
            query.isError,
            query.error,
            query.refetch,
        ],
    );

    return (
        <DiscountsContext.Provider value={value}>
            {children}
        </DiscountsContext.Provider>
    );
};

export { DiscountsProvider };
